import React from "react";
import { useLocation } from "react-router-dom";
import { safeImageUrl } from "../../../netlify/edge-shared/public-branding";
import { Helmet } from "../metadata/Helmet";
import { getCurrentBranding } from "../../utils/branding";

const SEOHead = ({
  priority = 2,
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = "website",
  twitterCard = "summary_large_image",
  structuredData,
  alternateUrls = {},
  noindex = false,
  nofollow = false,
  preloadAssets = [],
}) => {
  const location = useLocation();
  const branding = getCurrentBranding();
  const defaultTitle = branding.meta.title;
  const defaultDescription = branding.meta.description;
  const defaultKeywords = branding.meta.keywords;
  const defaultImage = branding.ogImage ||
    branding.logoFull ||
    "/favicon.svg";

  // Ensure all values are strings and not Symbols
  const safeTitle = typeof title === "string" ? title : "";
  const safeDescription = typeof description === "string" ? description : "";
  const safeKeywords = typeof keywords === "string" ? keywords : "";
  const safeCanonicalUrl = typeof canonicalUrl === "string" ? canonicalUrl : "";
  const safeOgImage = typeof ogImage === "string" ? ogImage : "";

  const finalTitle = safeTitle
    ? safeTitle.includes(branding.name)
      ? safeTitle
      : `${safeTitle} | ${branding.name}`
    : defaultTitle;
  const finalDescription = safeDescription || defaultDescription;
  const finalKeywords = safeKeywords || defaultKeywords;

  const siteUrl = branding.siteUrl;
  const finalCanonical =
    new URL(safeCanonicalUrl || location.pathname, siteUrl).href;
  const finalOgImage =
    new URL(safeImageUrl(safeOgImage, defaultImage), siteUrl).href;

  const isIndia = branding.locale === "en_IN";

  // Default structured data for organization
  const defaultStructuredData = structuredData || (branding.isPartner
    ? {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      name: branding.name,
      url: branding.siteUrl,
      logo: new URL(branding.logoFull, branding.siteUrl).href,
      description: branding.description,
    }
    : {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      name: branding.fullName,
      alternateName: branding.name,
      url: branding.siteUrl,
      logo: new URL(branding.logoFull, branding.siteUrl).href,
      description: branding.description,
      address: {
        "@type": "PostalAddress",
        addressLocality: branding.address.city,
        addressCountry: branding.geo.country,
      },
      contactPoint: {
        "@type": "ContactPoint",
        telephone: branding.phone,
        contactType: "customer service",
        availableLanguage: isIndia ? ["English", "Hindi"] : ["English"],
        areaServed: branding.geo.country,
      },
      sameAs: [
        branding.social.linkedin &&
        `https://www.linkedin.com/company/${branding.social.linkedin}`,
        branding.social.twitter &&
        `https://twitter.com/${branding.social.twitter.replace(/^@/, "")}`,
      ].filter(Boolean),
      accreditation: {
        "@type": "Organization",
        name: "Exemplar Global",
        url: "https://exemplarglobal.org",
      },
      courseMode: ["online", "blended"],
      educationalCredentialAwarded: "ISO Professional Certification",
      audience: {
        "@type": "Audience",
        audienceType: "Business professionals seeking ISO certification",
      },
    });

  const privatePage =
    /^\/(admin|user|instructor|partner|dashboard|auth|login|signup|checkout|cart)(\/|$)/
      .test(location.pathname) ||
    /^\/courses\/(player|preview|creation|completion)/.test(location.pathname);
  noindex = noindex || privatePage;
  const robotsContent = noindex || nofollow
    ? `${noindex ? "noindex" : "index"}, ${nofollow ? "nofollow" : "follow"}`
    : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";

  return (
    <Helmet priority={priority}>
      {/* Primary Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="title" content={finalTitle} />
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      {/* Canonical URL */}
      <link rel="canonical" href={finalCanonical} />
      {/* Robots */}
      <meta name="robots" content={robotsContent} />
      <meta
        name="googlebot"
        content={robotsContent}
      />
      {/* Geographic targeting */}
      {branding.geo.country && (
        <>
          <meta name="geo.region" content={branding.geo.region} />
          <meta name="geo.country" content={branding.geo.country} />
          <meta
            name="geo.placename"
            content={`${branding.geo.placename}, ${branding.geo.country}`}
          />
        </>
      )}
      <meta name="DC.title" content={finalTitle} />
      {/* Language and locale */}
      <meta
        httpEquiv="Content-Language"
        content={branding.locale.replace("_", "-")}
      />
      <meta name="language" content="English" />
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:image:alt" content={finalTitle} />
      <meta property="og:site_name" content={branding.name} />
      <meta property="og:locale" content={branding.locale} />
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={finalCanonical} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalOgImage} />
      <meta name="twitter:image:alt" content={finalTitle} />
      <meta
        name="twitter:site"
        content={branding.social.twitter
          ? `@${branding.social.twitter.replace(/^@/, "")}`
          : undefined}
      />
      <meta
        name="twitter:creator"
        content={branding.social.twitter
          ? `@${branding.social.twitter.replace(/^@/, "")}`
          : undefined}
      />
      {/* Additional SEO Tags */}
      <meta name="author" content={branding.fullName} />
      <meta name="publisher" content={branding.name} />
      <meta name="copyright" content={branding.name} />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="revisit-after" content="1 days" />
      {/* Mobile optimization */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={branding.name} />
      {/* Alternate URLs for different languages/regions */}
      {alternateUrls &&
        typeof alternateUrls === "object" &&
        Object.entries(alternateUrls)
          ?.filter(
            ([lang, url]) =>
              typeof lang === "string" && typeof url === "string",
          )
          ?.map(([lang, url]) => (
            <link
              key={lang}
              rel="alternate"
              hrefLang={lang}
              href={new URL(url, siteUrl).href}
            />
          ))}

      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="true"
      />
      <link rel="preconnect" href="https://www.google-analytics.com" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />
      {/* DNS prefetch for performance */}
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
      <link rel="dns-prefetch" href="https://cdn.jsdelivr.net" />

      {/* Preload Assets */}
      {preloadAssets &&
        Array.isArray(preloadAssets) &&
        preloadAssets.map((asset) => (
          <link key={asset} rel="preload" href={asset} as="image" />
        ))}

      {/* Structured Data */}
      <script type="application/ld+json">
        {(() => {
          try {
            return JSON.stringify(structuredData || defaultStructuredData);
          } catch (error) {
            console.warn("Error serializing structured data:", error);
            return JSON.stringify(defaultStructuredData);
          }
        })()}
      </script>
      {/* Google Analytics - Script is now loaded in index.html for better detection */}
      {/* We can still send page-specific configuration here if needed, but GA4 handles most of it automatically */}
      {/* Google Search Console verification */}
      {import.meta.env?.VITE_GOOGLE_SEARCH_CONSOLE_ID && (
        <meta
          name="google-site-verification"
          content={import.meta.env.VITE_GOOGLE_SEARCH_CONSOLE_ID}
        />
      )}
      {/* Bing Webmaster verification */}
      {import.meta.env?.VITE_BING_WEBMASTER_ID && (
        <meta
          name="msvalidate.01"
          content={import.meta.env.VITE_BING_WEBMASTER_ID}
        />
      )}
      {/* Yandex verification */}
      {import.meta.env?.VITE_YANDEX_VERIFICATION_ID && (
        <meta
          name="yandex-verification"
          content={import.meta.env.VITE_YANDEX_VERIFICATION_ID}
        />
      )}
    </Helmet>
  );
};

export default SEOHead;
